import type { UITabItem } from '@/core/data/config/uiConfig.interface';
import { TabItemType } from '@/core/data/config/uiConfig.interface';
import { gambits } from '@/core/gambits';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { GamStoreId } from '@/views/composables/constants/main/store.constants';
import type { GamTabItemType } from '@/views/composables/models/components/GamTab';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

export const useFilterStore = <T>(listId: GamListId) =>
  defineStore(`${GamStoreId.FILTER}-${listId}`, () => {
    const listTabItems = ref<GamTabItemType[] | null>(null);
    const filters = ref<T | null>();
    const router = useRouter();
    const route = useRoute();

    const init = () => {
      filters.value = getFilters();
    };

    const setListTabItems = (tabItems: UITabItem[] | null) => {
      listTabItems.value = tabItems;
      if (route.query.filter) {
        setActiveTab(String(route.query.filter) as TabItemType);
      } else {
        setActiveTab();
      }
    };

    const setActiveTab = (name?: TabItemType): void => {
      if (name) {
        listTabItems.value?.map((item) => {
          item.isActive = item.name === name;
        });
      } else if (listTabItems.value?.length) {
        listTabItems.value[0].isActive = true;
      }
    };

    const activeTab = computed(() => {
      const activeTabItem: GamTabItemType | undefined = listTabItems.value?.find(
        (item) => item.isActive && item.name !== TabItemType.ALL,
      );

      return activeTabItem?.name || TabItemType.ALL;
    });

    const getActiveTab = (): TabItemType | undefined => {
      const activeTabItem: GamTabItemType | undefined = listTabItems.value?.find(
        (item) => item.isActive && item.name !== TabItemType.ALL,
      );

      return activeTabItem?.name;
    };

    const getFilters = (): T | null | undefined => {
      const storageFilters = gambits.filterService.getSavedFilters<T>(listId);
      return filters.value || storageFilters;
    };

    const isFiltersApplied = (): boolean => {
      const filters = getFilters();
      if (!filters) return false;

      for (const key in filters) {
        if (filters[key] !== undefined) {
          return true;
        }
      }
      return false;
    };

    const setFilters = (newFilters?: T | null): void => {
      filters.value = newFilters;
      gambits.filterService.saveFiltersToStorage(listId, filters.value);
    };

    router.afterEach(() => {
      if (route.query.filter) {
        setActiveTab(String(route.query.filter) as TabItemType);
      } else {
        setActiveTab(TabItemType.ALL);
      }
    });

    return {
      listTabItems,
      filters,
      activeTab,
      init,
      setListTabItems,
      setActiveTab,
      getActiveTab,
      getFilters,
      setFilters,
      isFiltersApplied,
    };
  });
